import { useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import { SUPPORTED_COUNTRY_CODES, countryOptions, getCountryName } from "@/lib/countries";

export interface CountrySelectProps {
  /** Selected ISO 3166-1 alpha-2 code, or '' for none. */
  value: string;
  onValueChange: (code: string) => void;
  /** Codes to offer. Defaults to `SUPPORTED_COUNTRY_CODES`. */
  options?: readonly string[];
  /** Forwarded to the trigger so a `<Label htmlFor>` can target it. */
  id?: string;
  disabled?: boolean;
  /** Extra classes for the trigger (width, etc.). */
  className?: string;
  hasError?: boolean;
  /** Overrides the default "Select country" placeholder. */
  placeholder?: string;
}

/**
 * The one country picker. Codes come from `SUPPORTED_COUNTRY_CODES` (or the
 * `options` subset), names from `Intl.DisplayNames` in the user's language —
 * there is no name table, and nothing to translate when a country is added.
 * Sibling of `CurrencySelector`.
 *
 * A saved value outside `options` is still listed (so the trigger never goes
 * blank on legacy data); it just can't be chosen for another record.
 */
export function CountrySelect({
  value,
  onValueChange,
  options = SUPPORTED_COUNTRY_CODES,
  id,
  disabled = false,
  className,
  hasError = false,
  placeholder,
}: CountrySelectProps) {
  const { t, i18n } = useTranslation();
  // Optional so a key-echo test mock (`{ t }` without `i18n`) still renders.
  const language = i18n?.language;
  const code = value.trim().toUpperCase();

  const items = useMemo(() => {
    const codes = code && !options.includes(code) ? [...options, code] : options;
    return countryOptions(language, codes);
  }, [options, code, language]);

  return (
    <Select value={code} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger id={id} className={cn("w-full", hasError && "border-destructive", className)}>
        <SelectValue placeholder={placeholder ?? t("country.select")}>
          {code ? getCountryName(code, language) : undefined}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.code} value={item.code}>
            {item.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
